import {assert} from 'Asserts';
import {ProcedureRunPlanConditionREST} from 'procedures/network/procedureRunPlanDAO';
import {deriveConfirmationMethodByParamVersionId} from 'procedures/planning/buildFullExperimentPlan';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {ExperimentFlowchart} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {PlanConditionsByMethodId} from 'studies/typed/shared/procedure-runs/design-state/planConditionState';
import {getSharedConditionRows} from 'studies/typed/shared/procedure-runs/design-state/sharedPlanConditionView';

import {buildPlanMethodPlanRest} from './planConditionsSerializationUtils';

/**
 * Serializes preserved experimental inputs for a procedure or version change.
 *
 * Walks each method's rows. `position` is the row's dense array index.
 * Callers assign ids and clear unit-operation links after this returns. Values come from the preserved
 * inputs, not evaluated cells.
 */
export function serializePreservedInputsForNetwork(
  preservedInputs: ExperimentalInputs,
  planConditionsByMethodId: PlanConditionsByMethodId,
  methodIdsInOrder: ReadonlyArray<string>
): Array<ProcedureRunPlanConditionREST> {
  const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(preservedInputs);
  const payloads: Array<ProcedureRunPlanConditionREST> = [];
  methodIdsInOrder.forEach((methodId) => {
    const rows = planConditionsByMethodId[methodId] ?? [];
    rows.forEach((conditionRow, position) => {
      const {name, description, numReplicates} = conditionRow;
      const inputsForMethod = preservedInputs[methodId];
      assert(inputsForMethod != null, `Expected preserved experimental inputs for method ${methodId}`);
      payloads.push({
        name,
        description,
        position,
        templateMethodVersionId: methodId,
        replicateInfos: Array.from({length: numReplicates}, (_, replicateIndex) => ({
          replicateNumber: replicateIndex + 1,
        })),
        methodPlan: buildPlanMethodPlanRest(
          methodId,
          inputsForMethod,
          inputsForMethod.values[position],
          confirmationMethodByParamVersionId
        ),
      });
    });
  });
  return payloads;
}

/**
 * Maps each surviving method's local row to the persisted condition id that should be kept.
 *
 * The result is indexed by local position. A method with no row at that position is omitted.
 * Every row has an id, or none do. The id-less case is a plan with no stored conditions.
 * Primary match is a shared PMDV id. Fallback match is a stable flowchart node id.
 */
export function buildPreservedConditionIdMappings(
  planConditionsByMethodId: PlanConditionsByMethodId,
  oldFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart
): Array<Record<string, string>> {
  assertConditionIdsAreAllPresentOrAllAbsent(planConditionsByMethodId, oldFlowchart);
  const newMethodIdByOldMethodId = methodIdMapBetweenFlowcharts(oldFlowchart, newFlowchart);
  const maxPosition = oldFlowchart.nodes.reduce(
    (max, method) => Math.max(max, planConditionsByMethodId[method.id]?.length ?? 0),
    0
  );
  const mappings: Array<Record<string, string>> = Array.from({length: maxPosition}, () => ({}));
  oldFlowchart.nodes.forEach(({id: oldMethodId}) => {
    const newMethodId = newMethodIdByOldMethodId.get(oldMethodId);
    if (newMethodId == null) {
      return;
    }
    const conditionRows = planConditionsByMethodId[oldMethodId] ?? [];
    conditionRows.forEach((conditionRow, position) => {
      // No stored conditions: every row is id-less, so nothing is preserved.
      if (conditionRow.id == null) {
        return;
      }
      mappings[position][newMethodId] = conditionRow.id;
    });
  });
  return mappings;
}

/**
 * Moves each old method's rows onto the matching method in `newFlowchart`. Methods with no rows, and
 * new methods with no matching node, stay empty.
 */
export function remapPlanConditionsOntoFlowchart(
  planConditionsByMethodId: PlanConditionsByMethodId,
  oldFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart
): PlanConditionsByMethodId {
  const newMethodIdByOldMethodId = methodIdMapBetweenFlowcharts(oldFlowchart, newFlowchart);
  const remapped: PlanConditionsByMethodId = Object.fromEntries(
    newFlowchart.nodes.map((method) => [method.id, []])
  );
  oldFlowchart.nodes.forEach((oldMethod) => {
    const rows = planConditionsByMethodId[oldMethod.id] ?? [];
    if (rows.length === 0) {
      return;
    }
    const newMethodId = newMethodIdByOldMethodId.get(oldMethod.id);
    if (newMethodId == null) {
      return;
    }
    remapped[newMethodId] = rows.map((row) => ({...row}));
  });
  return remapped;
}

/**
 * Builds condition rows for a procedure or version change. Uniform plans keep every shared row on
 * every destination method. Non-uniform plans keep rows only on matching methods.
 */
export function planConditionsForFlowchartChange(
  planConditionsByMethodId: PlanConditionsByMethodId,
  oldFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart
): PlanConditionsByMethodId {
  const sharedRows = getSharedConditionRows(planConditionsByMethodId);
  const rowsByNewMethodId: PlanConditionsByMethodId =
    sharedRows == null
      ? remapPlanConditionsOntoFlowchart(planConditionsByMethodId, oldFlowchart, newFlowchart)
      : Object.fromEntries(
          newFlowchart.nodes.map((method) => [
            method.id,
            sharedRows.map((conditionRow) => ({...conditionRow})),
          ])
        );
  return Object.fromEntries(
    Object.entries(rowsByNewMethodId).map(([methodId, conditionRows]) => [
      methodId,
      conditionRows.map((conditionRow) => ({...conditionRow, id: undefined})),
    ])
  );
}

/** Rejects a plan that has an id on some condition rows and not others. */
function assertConditionIdsAreAllPresentOrAllAbsent(
  planConditionsByMethodId: PlanConditionsByMethodId,
  flowchart: ExperimentFlowchart
): void {
  const rows = flowchart.nodes.flatMap((method) => planConditionsByMethodId[method.id] ?? []);
  const rowsWithIds = rows.filter((row) => row.id != null);
  assert(
    rowsWithIds.length === 0 || rowsWithIds.length === rows.length,
    'Expected every condition row to have an id, or none of them'
  );
}

function methodIdMapBetweenFlowcharts(
  oldFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart
): Map<string, string> {
  const newMethodIds = new Set(newFlowchart.nodes.map((method) => method.id));
  const newMethodIdByNodeId = new Map(newFlowchart.nodes.map((method) => [method.nodeId, method.id]));
  const newMethodIdByOldMethodId = new Map<string, string>();
  oldFlowchart.nodes.forEach(({id: oldMethodId, nodeId}) => {
    if (newMethodIds.has(oldMethodId)) {
      newMethodIdByOldMethodId.set(oldMethodId, oldMethodId);
      return;
    }
    assert(nodeId != null, 'Expected every method to have a node ID');
    const newMethodId = newMethodIdByNodeId.get(nodeId);
    if (newMethodId != null) {
      newMethodIdByOldMethodId.set(oldMethodId, newMethodId);
    }
  });
  return newMethodIdByOldMethodId;
}
